import type { ReactNode } from 'react';
import { StatusDot } from '../StatusDot.tsx';
import { duration, relative } from '../format.ts';
import { isSupplyFault, stateWord } from './fault.ts';
import type { Read } from './read.ts';
import styles from './states.module.css';

/* ==========================================================================
   Per panel, not per page.

   THE BAR §6: the operator must never have to ask "is this now, and is this
   real?", and the answer is owed by every panel, because a console routinely
   shows one surface streaming beside one that stopped four minutes ago. A
   single connection light in the app header cannot say which.

   `ReadTag` is that answer in a Panel's 28px header line, so a source that
   has stopped costs zero vertical space to admit it. Four states, and the dot
   is hollow whenever nothing is arriving — the fill carries the fact before
   the colour does, and the colour is spent only on the panel that has stopped
   being current.
   ========================================================================== */

export function ReadTag({
  read,
  /** Print the countdown to the next automatic attempt. Leave it off where the
      surface banner is already showing one beside the recovery verb. */
  next = false,
  /**
   * NAME THE FAULT IN THE TAG — `⊙ no reply`, `⊙ timed out`, `⊙ server fault`
   * — instead of the neutral `No reading`.
   *
   * Off by default, and the default is the important half: eight panel headers
   * on one frame each repeating the diagnosis the banner two rows up has
   * already given is the restatement this module spent a round deleting (see
   * the note above, and `useBannerCarries`).
   *
   * On where the tag is the SURFACE'S ONLY liveness statement and has no fault
   * line under it to carry the cause — a status-bar tag in a footer strip,
   * where the alternative is `Polling off` over a source that never answered.
   * The word is `stateWord`'s, so the footer and the supply ledger below it
   * name one event once.
   */
  diagnose = false,
}: {
  read: Read;
  next?: boolean | undefined;
  diagnose?: boolean | undefined;
}) {
  const { phase, ageMs, everyMs, fetching, nextInMs } = read;

  /* A 404 IS AN ANSWER. The endpoint replied, in full, and the object is not
     there — so the tag says which answer it got, in grey, and never "No
     reply", which claims a broken link over a robot that was simply
     deregistered. Same for a 403. Only a failure of SUPPLY earns the amber. */
  const answered = read.fault !== null && !isSupplyFault(read.fault.class);


  /* WHAT THIS TAG IS FOR, AND WHAT IT IS NOT FOR.

     THE BAR §6 owes a liveness answer PER PANEL, because a console routinely
     shows one surface streaming beside one that stopped four minutes ago and a
     single light in the app header cannot say which. So this tag answers "is
     what I am reading current", per panel, always.

     It does NOT diagnose. It used to: every stopped panel wore `No reply`, so
     a cold start against a dead API printed the same two words eight times
     across one frame while the banner above said it a ninth. The diagnosis is
     the banner's, once; the fault's own scope is the panel's fault line, once.
     What is left here is the one thing that genuinely differs panel to panel —
     how old the figures under it are. */
  /* EVERY PANEL ANSWERS "IS THIS NOW", IN EVERY STATE.

     THE BAR §10 and tell #10: a screen with no per-panel liveness indicator is
     not authoritative, and the state where it matters most is the one where
     there is nothing under the header to judge. So a panel that has never
     landed says `No reading` — a FACT about the panel, in grey, not a
     diagnosis: which endpoint, which fault and how many runs it has lost are
     the fault line's job 8px below, and the sentence and the recovery are the
     banner's, once. The tag never repeats either. */
  /* A FIRST READ IS NOT ALWAYS A READ IN FLIGHT.

     A query the surface has disabled — `useTelemetry(undefined)` while the
     roster it would pick a robot from is down — has no data, no error and no
     request, and it reports `first` for the rest of the session. `Reading`
     over it is the console claiming to be working on something it is not going
     to do. The panel holds nothing and nothing is coming, which is the same
     fact `blank` states, so it gets the same word. */
  const word =
    phase === 'first'
      ? fetching
        ? 'Reading'
        : 'No reading'
      : phase === 'live'
        ? everyMs !== null && everyMs > 0
          ? `Polling ${duration(everyMs)}`
          : `Read ${relative(read.lastGoodIso)}`
        : answered
          ? read.fault?.status === null
            ? 'No record'
            : `Answered ${read.fault?.status ?? ''}`.trim()
          : phase === 'blank'
            ? /* The one place the tag may diagnose: see `diagnose`. Nothing has
                 landed, so there is no age to print in this slot and no fault
                 line under this tag to name the cause — `no reply` is both. */
              diagnose && read.fault !== null
              ? stateWord(read.fault.class)
              : 'No reading'
            : /* Not "Last read", which an operator reads as a normal age. The
                 figures under this header are real and are NOT current, and
                 that is the whole distinction this module exists to draw. */
              'Not current';

  /* A polled source normally says only its cadence — an age that is inside
     the cadence is noise. It starts printing the age at twice the interval,
     which is the point at which a read has actually been missed. */
  const overdue =
    phase === 'live' && everyMs !== null && everyMs > 0 && ageMs !== null && ageMs > everyMs * 2;

  /* AND HOW LONG IT HAS BEEN READING.

     The failure states have carried a measured age since the first round; the
     in-flight state carried none, so a hung endpoint wore `⊙ Reading` in its
     header for as long as the shift lasted and nothing on the panel moved but
     the app clock. Same slot, same mono, same rung as the stalled age below —
     the header answers "is this now" in every state, and "not yet, for 43s" is
     an answer. Withheld under a second, where it would read `0s` on every
     normal load and teach an operator to stop looking. */
  const waiting = phase === 'first' && fetching && read.waitingMs !== null && read.waitingMs >= 1_000;

  const age = waiting
    ? duration(read.waitingMs)
    : (phase === 'stalled' && !answered) || overdue
      ? duration(ageMs)
      : null;

  return (
    <span
      className={styles.tag}
      data-phase={answered ? 'answered' : phase}
      data-tone={answered ? 'answered' : phase === 'blank' ? 'absent' : phase}
      title={
        phase === 'live'
          ? `Last read ${relative(read.lastGoodIso)}`
          : phase === 'first'
            ? fetching
              ? `${read.path ?? 'This source'} has not answered yet this session — reading for ${duration(read.waitingMs ?? 0)}`
              : `${read.path ?? 'This source'} has not been read this session`
            : `${read.path ?? 'This source'} — ${read.fault?.reason ?? 'no reply'}; last good read ${relative(read.lastGoodIso)}`
      }
    >
      {/* Grey is a state (THE BAR §3): an answered 404 is a healthy endpoint
          reporting an absence, so its dot is `unknown`, not the amber of a
          source that has stopped supplying the console. Spending a fault
          colour on it devalues the colour on the panel that has really lost
          its link. */}
      <StatusDot
        tone={
          answered || phase === 'blank'
            ? 'neutral'
            : phase === 'stalled'
              ? 'warn'
              : 'nominal'
        }
        hollow={phase !== 'live'}
      />
      <span className={styles.tagWord}>{word}</span>
      {age !== null && <span className={styles.tagAge}>{age}</span>}
      {next && nextInMs !== null && !fetching && (
        <span className={styles.tagNext}>next {duration(nextInMs)}</span>
      )}
    </span>
  );
}


/* --------------------------------------------------------------------------
   Data that is real but is no longer arriving.

   The operator keeps every figure they were reading — throwing away a fleet
   because the newest poll timed out is worse than the outage — AND keeps it at
   full strength. A stopped panel is not a dimmed panel: a status colour is a
   claim about what the robot reported, not a claim about when, and the moment
   a link drops is the moment that reported picture matters most. Dimming it
   takes the answer away and calls it honesty.

   What the operator loses is the claim of CURRENCY, and three pieces of chrome
   carry it, no one of them colour alone:

     · the header tag above (`Not current 32s`, hollow dot, amber)
     · the fault row (Faults.tsx) — endpoint, fault, runs lost, age, re-read
     · the static screen-reader line below

   This component contributes the third, plus the one styling hook the rule
   still needs: `data-void`, for a viewport whose pixels ARE the reading.
   See the long note in states.module.css.
   -------------------------------------------------------------------------- */

export function NotCurrent({
  stopped,
  /**
   * The content is a camera frame or a point cloud rather than figures with an
   * age beside them, so a frozen frame reads as a live one. THE BAR §5: dim to
   * 40% and keep the age. The ONLY content this module dims.
   */
  viewport = false,
  /**
   * `stale` — real figures that have stopped refreshing.
   * `absent` — the layout with nothing in it: the instrument, standing in for
   *            a read that failed rather than one in flight.
   *
   * Both draw at full strength; the distinction is what the screen reader is
   * told, and what the fault row above says. Nothing on a settled read is
   * moving or shimmering, so an empty instrument cannot be mistaken for a
   * filling one without the help of alpha.
   */
  shape = 'stale',
  children,
}: {
  stopped: boolean;
  viewport?: boolean | undefined;
  shape?: 'stale' | 'absent' | undefined;
  children: ReactNode;
}) {
  return (
    <div className={styles.veil} data-stopped={stopped} data-void={viewport} data-shape={shape}>
      {/* The header tag and the fault row carry "not current" to the eye.
          Neither carried it to a screen reader, which read a stopped fleet
          table as a live one. One static line in document order, ahead of the
          figures it qualifies; NOT a live region, because it would then be
          re-announced on every poll for as long as the outage lasted. */}
      {stopped && shape === 'stale' && (
        <span className={styles.sr}>Figures below are not current.</span>
      )}
      {children}
    </div>
  );
}
